import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { workService } from '../services/workService';
import { Project } from '../types/work';
import DataTable from '../components/common/DataTable';
import { ProjectCard } from '../components/work/ProjectCard';
import { WorkStatusBadge } from '../components/work/WorkStatusBadge';
import { PriorityChip } from '../components/work/PriorityChip';
import { DueDateLabel } from '../components/work/DueDateLabel';
import { ViewToggle } from '../components/work/ViewToggle';
import { FilterPanel } from '../components/work/FilterPanel';
import { ContextStrip } from '../components/work/ContextStrip';
import UserBadge from '../components/common/UserBadge';
import { FiPlus, FiFolder, FiAlertCircle } from 'react-icons/fi';
import { ProjectDetailPanel } from '../components/work/ProjectDetailPanel';
import CreateWorkItemModal from '../components/work/CreateWorkItemModal';

const Projects: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const goalFilter = searchParams.get('goal');
  const [view, setView] = useState<'cards' | 'table'>('cards');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [panelProjectId, setPanelProjectId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data: projects = [], isLoading, isError, refetch } = useQuery(
    ['projects', goalFilter],
    () => workService.getProjects(goalFilter ? { goal_id: goalFilter } : {}),
  );

  // Fetch goal title for context strip
  const { data: goalData } = useQuery(
    ['goal', goalFilter],
    () => workService.getGoal(goalFilter!),
    { enabled: !!goalFilter },
  );

  const filtered = useMemo(() => {
    let result = projects;
    if (statusFilter.length) result = result.filter(p => statusFilter.includes(p.status));
    return result;
  }, [projects, statusFilter]);

  const toggleFilter = (val: string) => {
    setStatusFilter(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);
  };

  const columns: ColumnDef<Project, any>[] = [
    {
      accessorKey: 'title',
      header: 'Project',
      cell: ({ row }) => (
        <div>
          <span className="font-medium">{row.original.title}</span>
          {row.original.goal_id && (
            <div className="text-xs text-emerald-400 mt-0.5">🎯 linked to goal</div>
          )}
        </div>
      ),
    },
    { accessorKey: 'status', header: 'Status', cell: ({ row }) => <WorkStatusBadge status={row.original.status} /> },
    { accessorKey: 'priority', header: 'Priority', cell: ({ row }) => <PriorityChip priority={row.original.priority} /> },
    { accessorKey: 'due_date', header: 'Due', cell: ({ row }) => <DueDateLabel date={row.original.due_date} /> },
    {
      accessorKey: 'task_count',
      header: 'Tasks',
      cell: ({ row }) => <span className="text-sm text-gray-400">{row.original.task_count ?? 0}</span>,
    },
    { accessorKey: 'owner', header: 'Owner', cell: ({ row }) => <UserBadge owner={row.original.owner} size="sm" /> },
  ];

  return (
    <div className="space-y-4">
      {showCreate && <CreateWorkItemModal entityType="project" onClose={() => setShowCreate(false)} defaults={goalFilter ? { goal_id: goalFilter } : undefined} />}
      {panelProjectId && <ProjectDetailPanel projectId={panelProjectId} onClose={() => setPanelProjectId(null)} />}

      {/* Context strip */}
      {goalFilter && goalData && (
        <ContextStrip
          label={goalData.title}
          icon="🎯"
          accentColor="emerald"
          onDismiss={() => setSearchParams({})}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
            Projects
          </h1>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Organized work collections tied to goals
          </p>
        </div>
        <div className="flex items-center gap-3">
          <FilterPanel activeCount={statusFilter.length} onClear={() => setStatusFilter([])}>
            <div className="space-y-1">
              <label className="text-xs text-gray-400">Status</label>
              <div className="flex flex-wrap gap-1">
                {['draft', 'active', 'on_hold', 'completed', 'cancelled'].map(s => (
                  <button key={s} onClick={() => toggleFilter(s)}
                    className={`px-2 py-0.5 rounded text-xs ${statusFilter.includes(s) ? 'bg-teal-900/40 text-teal-300' : darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}
                  >{s}</button>
                ))}
              </div>
            </div>
          </FilterPanel>
          <ViewToggle view={view} onChange={setView} />
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-teal-600 to-cyan-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-teal-500/20 transition-shadow">
            <FiPlus className="w-3.5 h-3.5" /> New Project
          </button>
        </div>
      </div>

      {/* Content */}
      {isError ? (
        <div className="flex flex-col items-center py-20">
          <FiAlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Failed to load projects</h3>
          <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Check your connection and try again.</p>
          <button onClick={() => refetch()} className="px-4 py-2 rounded-lg bg-gray-700 text-white text-sm hover:bg-gray-600">Retry</button>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full" /></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center py-20">
          <FiFolder className={`w-12 h-12 mb-4 ${darkMode ? 'text-teal-400/40' : 'text-teal-300'}`} />
          <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>No projects yet</h3>
          <p className={`text-sm mb-6 max-w-md text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Projects break goals into trackable workstreams.
          </p>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 rounded-lg bg-gradient-to-r from-teal-600 to-cyan-500 text-white text-sm font-medium">
            Create your first project
          </button>
        </div>
      ) : view === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              goalTitle={goalData?.title}
              onClick={() => setPanelProjectId(project.id)}
            />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          onRowClick={(project: Project) => setPanelProjectId(project.id)}
          searchable
          searchPlaceholder="Search projects..."
          emptyMessage="No projects match your filters"
        />
      )}
    </div>
  );
};

export default Projects;
